import { appRoutes, fiduciarieMessages, useAppSelector } from 'azshared';
import type { DtaListedAccount } from 'azshared';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AppBreadcrumb } from '../../../../components/breadcrumb/app-breadcrumb';
import { AppButton, AppButtonType, AppButtonVariant } from '../../../../components/buttons/app-button';
import { Card, CardSize } from '../../../../components/card/card';
import { AppInputText } from '../../../../components/input-text/app-input-text';
import { BreadcrumbItem, mapBreadcrumb } from '../../../../utils/breadcrumb-item';
// Same layout as the create page, with the fields read-only.
import '../create/fiduciarie-create-page.scss';
import { accountFieldValue, accountFieldGroups } from '../fiduciarie-account-fields';
import { FiduciarieDeleteModal } from '../fiduciarie-delete-modal';

export const FiduciarieDetailPage = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { username } = useParams();
  // There is no API to read a single account: it is taken from the results of the search that listed it.
  const account = useAppSelector((state) => state.features.adminConsole.fiduciarie.searchDtaResponse?.data.find((listed) => listed.username === username));
  const [accountToDelete, setAccountToDelete] = useState<DtaListedAccount>();

  const handleBack = useCallback(() => {
    navigate(appRoutes.AdminFiduciarie);
  }, [navigate]);

  const handleDelete = useCallback(() => {
    setAccountToDelete(account);
  }, [account]);

  const handleCancelDelete = useCallback(() => {
    setAccountToDelete(undefined);
  }, []);

  const handleDeleted = useCallback(() => {
    navigate(appRoutes.AdminFiduciarie, { replace: true });
  }, [navigate]);

  // Reached by URL or after a reload there are no search results to read it from: back to the list.
  // Also after the delete, which drops the account from the results.
  if (!account) {
    return <Navigate to={appRoutes.AdminFiduciarie} replace />;
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    mapBreadcrumb['gestione-fiduciarie'],
    {
      key: 'dettaglio',
      label: fiduciarieMessages.breadcrumbDetail,
      path: generatePath(appRoutes.AdminFiduciarieDetail, { username: account.username })
    }
  ];

  return (
    <div className='fiduciarie-create-page fiduciarie-detail-page'>
      <div className='fiduciarie-create-card'>
        <AppBreadcrumb customItems={breadcrumbItems} />
        <Card title={formatMessage(fiduciarieMessages.pageTitle)} sizeVariant={CardSize.MAXI}>
          <div className='fiduciarie-form'>
            <div className='card-form-title'>{formatMessage(fiduciarieMessages.cardTitleDetail, { username: account.username })}</div>
            {accountFieldGroups.map((group) => (
              <fieldset key={group.key} className='fiduciarie-form-section'>
                <legend className='fiduciarie-form-section-title'>{formatMessage(group.title)}</legend>
                <div className='fiduciarie-form-grid'>
                  {group.fields.map((field) => (
                    <AppInputText
                      key={field.key}
                      value={accountFieldValue(account, field.key) ?? ''}
                      readOnly
                      label={formatMessage(field.label)}
                      placeholder={formatMessage(field.placeholder)}
                    />
                  ))}
                </div>
              </fieldset>
            ))}

            <div className='form-buttons-row'>
              <AppButton
                type={AppButtonType.BUTTON}
                variant={AppButtonVariant.Secondary}
                label={formatMessage(fiduciarieMessages.buttonBack)}
                onClick={handleBack}
              />
              <AppButton type={AppButtonType.BUTTON} label={formatMessage(fiduciarieMessages.buttonDelete)} onClick={handleDelete} />
            </div>
          </div>
        </Card>
      </div>

      <FiduciarieDeleteModal account={accountToDelete} onClose={handleCancelDelete} onDeleted={handleDeleted} />
    </div>
  );
};
